import {
  createBranchMutation,
  getBranchesQuery,
  handleApiResponse,
  queryKeys,
} from "@comandero/shared";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { type Branch, desc, type Organization, organization } from "@comandero/powersync";
import { type Accessor, createEffect, createMemo } from "solid-js";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { activeRestaurantIdFor, setActiveRestaurantIdFor } from "@/features/pos/active-restaurant";

export interface ActiveContext {
  /** Restaurante (organización) activo. */
  org: Accessor<Organization | null>;
  /** Todos los restaurantes del usuario sincronizados en local (switcher). */
  orgs: Accessor<Organization[]>;
  /** Sucursal activa. */
  branch: Accessor<Branch | null>;
  branches: Accessor<Branch[]>;
  isReady: Accessor<boolean>;
  /** Cambia el restaurante activo (persiste por usuario; re-scopea todo el POS). */
  setActiveRestaurant: (orgId: string) => void;
  /**
   * Crea un restaurante (org atómica server-side), espera a que sincronice a
   * local, lo deja como ACTIVO y devuelve su id. Sirve tanto para el
   * onboarding (primer restaurante) como para añadir restaurantes adicionales.
   */
  bootstrapRestaurant: (name: string) => Promise<string>;
  /** Crea una sucursal adicional en el restaurante activo. */
  addBranch: (name: string) => Promise<string | null>;
}

/**
 * Guard A NIVEL DE MÓDULO de la sucursal por defecto: con el switcher en el
 * header hay VARIAS instancias vivas de `useActiveContext` a la vez (header +
 * página); un guard por instancia dejaría que cada una creara su "Principal"
 * duplicada para la misma org nueva.
 */
const autoBranchHandled = new Set<string>();

/**
 * Contexto operativo del POS: restaurante (org) + sucursal activos.
 *
 * Modelo org-céntrico: el restaurante es la organización; el `member` se crea
 * atómicamente en el server (CC-3) vía `createOrganization`. La sucursal por
 * defecto se crea en local y sincroniza por la cola CRUD.
 *
 * Multi-restaurante: el usuario puede pertenecer a varias orgs; el activo es
 * una preferencia persistida por usuario (`active-restaurant.ts`) validada
 * contra las orgs en local (fallback: la primera). Todas las queries de
 * dominio derivan de `org()`/`branch()`, así que un switch re-scopea el POS
 * completo de forma reactiva.
 */
export function useActiveContext(): ActiveContext {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();

  const orgsQuery = createPowerSyncQuery<Organization>({
    queryKey: () => queryKeys.comandero.organizations(),
    queryFn: () => {
      const d = drizzle();
      if (!d) {
        return null;
      }
      return d.select().from(organization).orderBy(desc(organization.created_at));
    },
    db,
  });

  const orgs = createMemo<Organization[]>(() => orgsQuery.data ?? []);

  const org = createMemo<Organization | null>(() => {
    const all = orgs();
    if (all.length === 0) {
      return null;
    }
    // Preferencia persistida validada contra lo que existe en local; si el
    // puntero quedó viejo (org borrada/no sincronizada) cae a la primera:
    // nunca bloquea (auto-sanación, mismo criterio que el puntero de ticket).
    const preferred = activeRestaurantIdFor(session.userId());
    return all.find((candidate) => candidate.id === preferred) ?? all[0]!;
  });

  const setActiveRestaurant = (orgId: string): void => {
    setActiveRestaurantIdFor(session.userId(), orgId);
  };

  const branchesQuery = createPowerSyncQuery<Branch>({
    queryKey: () => queryKeys.comandero.branches(org()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const o = org();
      if (!d || !o?.id) {
        return null;
      }
      return getBranchesQuery(d, o.id);
    },
    db,
    enabled: () => Boolean(org()?.id),
  });

  const branches = createMemo<Branch[]>(() => branchesQuery.data ?? []);
  const branch = createMemo<Branch | null>(() => branches()[0] ?? null);
  const isReady = createMemo(() => Boolean(org()?.id) && Boolean(branch()?.id));

  // Red de seguridad: si la org ya está en local pero aún no tiene sucursal
  // (p. ej. el alta creó la org pero la sucursal no llegó a escribirse), crea
  // una por defecto. Idempotente entre TODAS las instancias del hook (guard de
  // módulo): solo dispara una vez por org.
  createEffect(() => {
    const d = drizzle();
    const o = org();
    if (!d || !o?.id) {
      return;
    }
    if (branchesQuery.isFetching) {
      return;
    }
    if (branches().length > 0) {
      return;
    }
    if (autoBranchHandled.has(o.id)) {
      return;
    }
    autoBranchHandled.add(o.id);
    void createBranchMutation(d, o.id, "Principal");
  });

  async function bootstrapRestaurant(name: string): Promise<string> {
    const d = drizzle();
    if (!d) {
      throw new Error("Local database is not ready");
    }
    // Con multi-restaurante ya puede haber orgs en local: capturamos las ids
    // previas para esperar a la org NUEVA (no a "cualquier org", que con una
    // existente resolvería al instante con la equivocada).
    const before = new Set(orgs().map((existing) => existing.id));
    const slug = name
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 40);
    // `openapi-fetch` NO lanza ante 4xx/5xx (devuelve `{error}`): sin este
    // wrapper, un 401 (p. ej. sesión caducada) moría en silencio y el poll de
    // abajo esperaba 30s a una org que jamás llegaría — el botón "Create" se
    // quedaba pegado sin feedback (bug del onboarding, familia CC-8).
    await handleApiResponse(
      api.POST("/api/org/createOrganization", {
        body: { name: name.trim(), slug: slug || `r-${Date.now()}` },
      }),
    );
    // La respuesta solo confirma `success`; el id llega cuando la org sincroniza
    // a local. Esperamos a que aparezca; la sucursal por defecto la crea el
    // efecto reactivo de arriba en cuanto la org existe en local.
    const orgId = await waitForNewOrgLocal(d, before);
    if (!orgId) {
      throw new Error("Restaurant did not sync to local in time");
    }
    // El recién creado pasa a ser el activo (onboarding: era el único; alta
    // adicional: el usuario acaba de pedirlo — el switch es la expectativa).
    setActiveRestaurant(orgId);
    return orgId;
  }

  async function addBranch(name: string): Promise<string | null> {
    const d = drizzle();
    const o = org();
    if (!d || !o?.id) {
      return null;
    }
    return createBranchMutation(d, o.id, name);
  }

  return {
    org,
    orgs,
    branch,
    branches,
    isReady,
    setActiveRestaurant,
    bootstrapRestaurant,
    addBranch,
  };
}

const POLL_INTERVAL_MS = 200;
const MAX_WAIT_MS = 30_000;

type DrizzleClient = NonNullable<ReturnType<ReturnType<typeof useDrizzle>>>;

/**
 * Espera (con polling local) a que aparezca una organización que NO estaba en
 * `before` y devuelve su id (la más reciente de las nuevas). En el alta es la
 * recién creada, aunque el usuario ya tuviera otros restaurantes.
 */
async function waitForNewOrgLocal(
  d: DrizzleClient,
  before: ReadonlySet<string>,
): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    const rows = await d.select().from(organization).orderBy(desc(organization.created_at));
    const fresh = rows.find((row) => !before.has(row.id));
    if (fresh?.id) {
      return fresh.id;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
